import type { ReactNode } from "react"
import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import { CUSTOMER_VIEW_TAB_PARAM } from "@/v2-backoffice-client/customer/customer.links"
import {
  describeCarrierScope,
  describeCarrierSeatHolders,
  describeFreeSource,
  describeLicenseSource,
  describeLicensingTabLink,
  describeOwnListSource,
  type CustomerFeatureAccessSource,
} from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import {
  groupSeatHoldersByLicense,
  type LicenseSeatHolder,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"

export type CustomerFeatureAccessHeadlineProps = {
  access: CustomerFeatureAccessFragment
  customerName: string
  /** Seats held across the customer's seated licenses, named under their carrier. */
  seatHolders?: readonly LicenseSeatHolder[]
  /** The "show access for" picker, when two users of the customer can differ. */
  picker?: ReactNode
}

// What this customer's users can use, said before any list: the three
// places access comes from, one row each — free for everyone, their
// licenses (named, with what kind of thing each is and which license it
// rides on, and for a seated license WHO holds a seat), and what is added
// on this tab — so a salesperson can read the answer out, and sees where to
// go to change each part. The lists below are the same three sources row
// by row; this is the sentence they add up to. Each source is its own
// ruled row with the label at the top left, so the three read as sections
// even when the license row runs to several lines.
function CustomerFeatureAccessHeadline(
  props: CustomerFeatureAccessHeadlineProps,
) {
  const { access, customerName, seatHolders, picker } = props
  const sources: CustomerFeatureAccessSource[] = [
    describeFreeSource(access),
    describeLicenseSource(access),
    describeOwnListSource(access, customerName),
  ]
  const holdersByLicenseId = groupSeatHoldersByLicense(seatHolders ?? [])

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 pb-1 pt-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2.5">
        <h3 className="text-sm font-medium">
          What {customerName}&apos;s users can use
        </h3>
        <div className="flex-1" />
        {picker}
      </div>
      <dl className="divide-y divide-border/70 border-t border-border/70 text-xs">
        {sources.map((source) => (
          <div
            key={source.key}
            className="grid grid-cols-[9rem_1fr] gap-x-4 py-2.5"
          >
            <dt
              className={cn(
                "flex items-start gap-1.5 font-medium",
                !source.applies && "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "mt-px flex size-3.5 shrink-0 items-center justify-center",
                  source.applies ? "text-primary" : "text-muted-foreground",
                )}
                aria-hidden
              >
                <Icon src={source.applies ? "check" : "cross"} size={11} />
              </span>
              {source.label}
            </dt>
            <dd className="min-w-0 text-muted-foreground">
              {source.key === "licenses" && access.Carriers.length > 0 ? (
                // One carrier per line: a salesperson reads these out, and
                // a run of names with separators was not readable. A seated
                // carrier names its holders on the line below.
                <ul className="flex flex-col gap-1">
                  {access.Carriers.map((carrier) => (
                    <li key={`${carrier.Kind}:${carrier.Name}`}>
                      <span className="text-foreground">{carrier.Name}</span>
                      {" — "}
                      {describeCarrierScope(carrier)}
                      {carrier.SeatHoldersOnly && (
                        <div className="mt-0.5 border-l-2 border-border pl-2">
                          {describeCarrierSeatHolders(
                            carrier,
                            holdersByLicenseId.get(carrier.LicenseId) ?? [],
                            customerName,
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                source.text
              )}
              {source.key === "licenses" && (
                <Link
                  variant="muted"
                  size="xs"
                  iconSrc={null}
                  to={`?${CUSTOMER_VIEW_TAB_PARAM}=billing`}
                  className="mt-1 underline underline-offset-2"
                >
                  {describeLicensingTabLink(access)}
                </Link>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default CustomerFeatureAccessHeadline
